/**
 * Grid operations (Surfer-style) — math between grids, volume, residuals,
 * blanking, and profile extraction. Operates on gridding results whose
 * geojson.metadata carries { grid: number[][], bbox: [minLon,minLat,maxLon,maxLat] }.
 */
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

/** Extract { grid, bbox } from a gridding result layer's geojson; null if absent. */
export function gridFromResult(geojson) {
  const md = geojson?.metadata;
  if (!md?.grid || !md?.bbox) return null;
  return { grid: md.grid, bbox: md.bbox };
}

/**
 * Every result layer that carries a usable grid (`metadata.grid` + `metadata.bbox`), whatever
 * its `kind` or the panel that made it (Gridding, Variogram "tuned" kriging, grid math, jobs).
 */
export function gridResultLayers(resultLayers = []) {
  return resultLayers.filter((r) => {
    const g = gridFromResult(r?.geojson);
    return !!g && Array.isArray(g.grid) && g.grid.length > 1 && Array.isArray(g.grid[0]);
  });
}

/**
 * Y-axis domain for a profile chart: data min..max with `pad` (fraction of the range) on
 * both sides, so an elevation profile is not flattened by an axis that starts at 0.
 * Falls back to ['auto', 'auto'] without finite values.
 */
export function paddedDomain(values, pad = 0.05) {
  let lo = Infinity, hi = -Infinity;
  (values || []).forEach((v) => {
    if (typeof v === 'number' && Number.isFinite(v)) { if (v < lo) lo = v; if (v > hi) hi = v; }
  });
  if (!Number.isFinite(lo)) return ['auto', 'auto'];
  const span = hi - lo || Math.abs(hi) * 0.1 || 1;
  return [lo - span * pad, hi + span * pad];
}

/** Bilinear sample of a grid at lon/lat. Returns null outside the bbox. */
export function sampleGrid(grid, bbox, lon, lat) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const rows = grid.length, cols = grid[0].length;
  if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) return null;

  const fx = ((lon - minLon) / (maxLon - minLon)) * (cols - 1);
  const fy = ((lat - minLat) / (maxLat - minLat)) * (rows - 1);
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(cols - 1, x0 + 1), y1 = Math.min(rows - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;

  const v00 = grid[y0][x0], v10 = grid[y0][x1], v01 = grid[y1][x0], v11 = grid[y1][x1];
  if ([v00, v10, v01, v11].some(v => v === null || v === undefined || Number.isNaN(v))) return null;

  return v00 * (1 - tx) * (1 - ty) + v10 * tx * (1 - ty) + v01 * (1 - tx) * ty + v11 * tx * ty;
}

/**
 * Element-wise combination of two grids. Grids must share dimensions;
 * if bboxes differ, grid B is resampled onto grid A's cells.
 * @param {object} a - { grid, bbox }
 * @param {object} b - { grid, bbox }
 * @param {(va:number, vb:number)=>number} fn
 * @returns {{ grid, bbox }}
 */
export function combineGrids(a, b, fn) {
  const rows = a.grid.length, cols = a.grid[0].length;
  const [minLon, minLat, maxLon, maxLat] = a.bbox;
  const sameShape = b.grid.length === rows && b.grid[0].length === cols
    && a.bbox.every((v, i) => Math.abs(v - b.bbox[i]) < 1e-12);

  const out = [];
  for (let i = 0; i < rows; i++) {
    const row = [];
    for (let j = 0; j < cols; j++) {
      const va = a.grid[i][j];
      let vb;
      if (sameShape) {
        vb = b.grid[i][j];
      } else {
        const lon = minLon + (j / (cols - 1)) * (maxLon - minLon);
        const lat = minLat + (i / (rows - 1)) * (maxLat - minLat);
        vb = sampleGrid(b.grid, b.bbox, lon, lat);
      }
      if (va === null || vb === null || Number.isNaN(va) || Number.isNaN(vb) || vb === undefined) {
        row.push(NaN);
      } else {
        const v = fn(va, vb);
        row.push(typeof v === 'number' && isFinite(v) ? v : NaN);
      }
    }
    out.push(row);
  }
  return { grid: out, bbox: a.bbox };
}

/** Rebuild a gridding-result GeoJSON (point FC + metadata) from a raw grid. */
export function gridToResultGeoJSON(grid, bbox, propertyName = 'value') {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const rows = grid.length, cols = grid[0].length;
  let vmin = Infinity, vmax = -Infinity;
  grid.forEach(row => row.forEach(v => {
    if (v === null || Number.isNaN(v)) return;
    if (v < vmin) vmin = v;
    if (v > vmax) vmax = v;
  }));
  if (!isFinite(vmin)) { vmin = 0; vmax = 1; }

  const features = [];
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const v = grid[i][j];
      if (v === null || v === undefined || Number.isNaN(v)) continue;
      const norm = vmax > vmin ? (v - vmin) / (vmax - vmin) : 0.5;
      features.push({
        type: 'Feature',
        properties: { [propertyName]: Math.round(v * 1e4) / 1e4, normalized: Math.round(norm * 1e4) / 1e4 },
        geometry: {
          type: 'Point',
          coordinates: [
            Math.round((minLon + (j / (cols - 1)) * (maxLon - minLon)) * 1e6) / 1e6,
            Math.round((minLat + (i / (rows - 1)) * (maxLat - minLat)) * 1e6) / 1e6,
          ],
        },
      });
    }
  }

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      min: Math.round(vmin * 1e4) / 1e4,
      max: Math.round(vmax * 1e4) / 1e4,
      grid_size: [cols, rows],
      grid,
      bbox,
    },
  };
}

/**
 * Volume of the grid surface above/below a datum (numerical integration).
 * Returns volumes in value-units × m².
 */
export function gridVolume(grid, bbox, datum = 0) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const rows = grid.length, cols = grid[0].length;
  const midLat = (minLat + maxLat) / 2;
  const mLat = 110540, mLon = 111320 * Math.cos((midLat * Math.PI) / 180);
  const cellArea = ((maxLon - minLon) * mLon / (cols - 1)) * ((maxLat - minLat) * mLat / (rows - 1));

  let above = 0, below = 0, cells = 0;
  grid.forEach(row => row.forEach(v => {
    if (v === null || Number.isNaN(v)) return;
    cells++;
    const d = v - datum;
    if (d > 0) above += d * cellArea;
    else below += -d * cellArea;
  }));

  return { above, below, net: above - below, cellArea, cells };
}

/**
 * Residuals: observed point values minus the grid prediction at each point.
 * @returns array of { lon, lat, observed, predicted, residual } (null-prediction points skipped)
 */
export function gridResiduals(points, grid, bbox) {
  return points.map(p => {
    const predicted = sampleGrid(grid, bbox, p.lon, p.lat);
    if (predicted === null) return null;
    return { ...p, predicted, residual: p.value - predicted };
  }).filter(Boolean);
}

/**
 * Blank (mask) a grid outside polygon boundaries — cells outside become NaN.
 * @param {object} g - { grid, bbox }
 * @param {Array} polygons - GeoJSON Polygon/MultiPolygon features
 */
export function blankGridOutside(g, polygons) {
  const { grid, bbox } = g;
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const rows = grid.length, cols = grid[0].length;
  const out = grid.map((row, i) => row.map((v, j) => {
    if (v === null || Number.isNaN(v)) return NaN;
    const pt = {
      type: 'Point',
      coordinates: [
        minLon + (j / (cols - 1)) * (maxLon - minLon),
        minLat + (i / (rows - 1)) * (maxLat - minLat),
      ],
    };
    const inside = polygons.some(poly => {
      try { return booleanPointInPolygon(pt, poly); } catch { return false; }
    });
    return inside ? v : NaN;
  }));
  return { grid: out, bbox };
}

/**
 * Extract a profile of grid values along a polyline.
 * @param {object} g - { grid, bbox }
 * @param {Array} coords - [[lon,lat], ...] polyline in WGS84
 * @param {number} nSamples
 * @returns array of { dist (m), value, lon, lat }
 */
export function profileAlongLine(g, coords, nSamples = 120) {
  if (!coords || coords.length < 2) return [];
  const midLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const mLat = 110540, mLon = 111320 * Math.cos((midLat * Math.PI) / 180);

  // Cumulative distances in meters
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    const dx = (coords[i][0] - coords[i - 1][0]) * mLon;
    const dy = (coords[i][1] - coords[i - 1][1]) * mLat;
    cum.push(cum[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }
  const total = cum[cum.length - 1] || 1;

  const positionAt = (d) => {
    let i = 1;
    while (i < cum.length && cum[i] < d) i++;
    if (i >= cum.length) return coords[coords.length - 1];
    const t = (d - cum[i - 1]) / ((cum[i] - cum[i - 1]) || 1);
    return [
      coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
      coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
    ];
  };

  const samples = [];
  for (let s = 0; s <= nSamples; s++) {
    const d = (s / nSamples) * total;
    const [lon, lat] = positionAt(d);
    const value = sampleGrid(g.grid, g.bbox, lon, lat);
    samples.push({ dist: Math.round(d * 10) / 10, value, lon, lat });
  }
  return samples;
}

/**
 * Sample a grid every `stepM` metres along a polyline (end point included).
 * Same maths as the backend (`grid_profile.py`): equirectangular metres, bilinear
 * values, NaN outside the grid or next to a no-data cell.
 * @returns array of { dist (m), value (number|NaN), lon, lat }
 */
export function profileAlongLineStep(g, coords, stepM = 10) {
  if (!coords || coords.length < 2 || !(stepM > 0)) return [];
  const midLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const mLat = 110540, mLon = 111320 * Math.cos((midLat * Math.PI) / 180);
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    cum.push(cum[i - 1] + Math.hypot((coords[i][0] - coords[i - 1][0]) * mLon, (coords[i][1] - coords[i - 1][1]) * mLat));
  }
  const total = cum[cum.length - 1];
  if (!(total > 0)) return [];
  const dists = [];
  for (let d = 0; d <= total + 1e-9; d += stepM) dists.push(Math.min(d, total));
  if (total - dists[dists.length - 1] > 1e-9) dists.push(total);
  let seg = 1;
  return dists.map((d) => {
    while (seg < cum.length - 1 && cum[seg] < d) seg++;
    const t = (d - cum[seg - 1]) / ((cum[seg] - cum[seg - 1]) || 1);
    const lon = coords[seg - 1][0] + (coords[seg][0] - coords[seg - 1][0]) * t;
    const lat = coords[seg - 1][1] + (coords[seg][1] - coords[seg - 1][1]) * t;
    const v = sampleGrid(g.grid, g.bbox, lon, lat);
    return { dist: Math.round(d * 100) / 100, value: v === null ? NaN : v, lon, lat };
  });
}
